import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { closeMediaViewModal } from '../actions/modal';
import IconToolTip from './IconToolTip';
import OverflowTip from './OverflowTip';
import { chatType, defaultSingleChatPic } from '../pages/chat/ChatController';
import { getFileType, getLookupWitHDefaultValue, GetQueryParam, modulo, validateStringValue } from '../utils/common';
import moment from 'moment';
import { downloadFiles, transformAttachmentAction } from '../actions/utils';
import ImageZoom from './ImageZoom';
import ApprovedRejectIcon from '../pages/chat/components/ApprovedRejectIcon';
import { getImageStyle } from '../pages/chat/messageBox/Messages/AttachmentMessage/Image';
import { useParams } from 'react-router-dom';
import { TicketAgentRole } from '../pages/Ticket/TicketController';
import useHeicImage from '../hooks/useHeicImage';

const MediaView = (props) => {
    let { sender, attachments, openedMediaIndex = 0, showSenderDetail, currentConversationId } = useSelector((state) => state.modal);
    const [viewMediaIndex, setViewMediaIndex] = useState(openedMediaIndex);
    const dispatch = useDispatch();
    const mediaViewRef = useRef(null);

    const onDownloadClick = () => {
        const currentFile = attachments[viewMediaIndex];
        const file = { url: currentFile.url, fileName: currentFile.name, mimeType: currentFile.mime_type };
        dispatch(downloadFiles({ files: [file] }));
    };

    const handleArrowClick = useCallback(
        (arrowDirection) => {
            const increment = arrowDirection === 'right' ? 1 : -1;
            const attachmentsArray = attachments.length;
            const nextIndex = modulo(viewMediaIndex + increment, attachmentsArray);
            setViewMediaIndex(nextIndex);
        },
        [attachments?.length, viewMediaIndex]
    );

    const closeMedia = (event) => {
        if (event.keyCode === 27 && event.key === 'Escape') {
            dispatch(closeMediaViewModal());
        } else if (event.keyCode === 39 && event.key === 'ArrowRight') {
            handleArrowClick('right');
        } else if (event.keyCode === 37 && event.key === 'ArrowLeft') {
            handleArrowClick('left');
        }
    };

    useEffect(() => {
        if (mediaViewRef?.current) {
            mediaViewRef.current.focus();
        }
    }, []);

    return (
        <div ref={mediaViewRef} tabIndex={-1} onKeyDown={closeMedia} className="h-screen">
            <div className='fixed inset-0 z-50 bg-black bg-opacity-95'></div>
            <div className='fixed inset-0 z-50 outline-none focus:outline-none flex flex-col'>
                <HeaderComponent onDownloadClick={onDownloadClick} showSenderDetail={showSenderDetail} viewMediaIndex={viewMediaIndex} attachments={attachments} sender={sender} />
                <FilesComponent attachments={attachments} viewMediaIndex={viewMediaIndex} handleArrowClick={handleArrowClick} currentConversationId={currentConversationId} />
                <Footer attachments={attachments} viewMediaIndex={viewMediaIndex} setViewMediaIndex={setViewMediaIndex} />
            </div>
        </div>
    );
};

export default MediaView;

const ThumbnailWithHeic = ({ url, mimeType, type }) => {
    const { src: renderSrc, isConverting } = useHeicImage(url, mimeType);

    return (
        <div className="relative h-full w-full">
            {renderSrc && !isConverting && <GetComponentByType type={type} url={renderSrc} />}
            {isConverting && (
                <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 text-white text-xs'>
                    <span className='material-icons text-sm animate-spin'>refresh</span>
                </div>
            )}
        </div>
    );
};

const Footer = ({ attachments, viewMediaIndex, setViewMediaIndex }) => {
    return (
        <div className='py-8 bg-gray-800 bg-opacity-30 backdrop-filter backdrop-blur-sm border-t border-gray-700'>
            <div className='flex items-center justify-center px-6 overflow-x-auto overflow-y-hidden'>
                <div className='flex items-center gap-4 max-w-full'>
                    {attachments?.map((item, index) => {
                        const type = getFileType(item.mime_type);

                        return (
                            <div
                                key={item.id}
                                className={`relative cursor-pointer transition-all duration-200 `}
                                style={{ height: '60px', width: '60px', border: index === viewMediaIndex ? '2px solid #FF9800' : 'none' }}
                            >
                                <div
                                    onClick={() => setViewMediaIndex(index)}
                                    className={`h-full w-full rounded-md overflow-hidden`}
                                >
                                    <ThumbnailWithHeic url={item.url} mimeType={item.mime_type} type={type} />
                                </div>
                                {index === viewMediaIndex && (
                                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-scogoorange"></div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const HeaderComponent = ({ onDownloadClick, attachments, viewMediaIndex, showSenderDetail, sender }) => {
    const dispatch = useDispatch();
    const { conversationDetails } = useSelector((state) => state.conversations);

    const getSenderDetails = () => {
        if (!sender) {
            sender = attachments?.[viewMediaIndex]?.message?.from;
        }
        const conversation = conversationDetails[attachments[viewMediaIndex].conversation];
        const messageTime = moment(attachments[viewMediaIndex].createdAt).format('DD/MM/YYYY hh:mm A');
        const messageTitle = `${validateStringValue(sender?.username)} @ ${validateStringValue(conversation?.conversationName)}`;
        const senderProfilePic = defaultSingleChatPic;

        return (
            <div className='flex items-center gap-4'>
                <div className="h-12 w-12 rounded-full overflow-hidden border-2 border-gray-200">
                    <img className='h-full w-full object-cover' alt='profile' src={senderProfilePic} />
                </div>
                <div className='flex flex-col'>
                    {messageTitle && <span className='text-lg text-white font-medium'>{messageTitle}</span>}
                    {messageTime && <span className='text-sm text-gray-300'>{messageTime}</span>}
                </div>
            </div>
        );
    };

    const currentFileName = validateStringValue(attachments?.[viewMediaIndex]?.fileName);

    return (
        <div className='px-6 py-4 bg-gray-900 bg-opacity-80 backdrop-filter backdrop-blur-sm border-b border-gray-700 flex items-center justify-between'>
            <div className='flex items-center gap-4 max-w-[70%]'>
                {showSenderDetail ? getSenderDetails() : (
                    <div className="flex items-center">
                        <span className="material-icons text-gray-300 mr-2">insert_drive_file</span>
                        <span className='text-lg text-white font-medium truncate'>{currentFileName}</span>
                    </div>
                )}
            </div>
            <div className='flex gap-4 items-center'>
                <IconToolTip title='Download'>
                    <button
                        onClick={onDownloadClick}
                        className='flex items-center justify-center h-10 w-10 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors duration-200'
                    >
                        <span className='material-icons text-white text-xl'>download</span>
                    </button>
                </IconToolTip>
                <IconToolTip title='Close'>
                    <button
                        onClick={() => dispatch(closeMediaViewModal())}
                        className='flex items-center justify-center h-10 w-10 rounded-full bg-gray-800 hover:bg-gray-700 transition-colors duration-200'
                    >
                        <span className='material-icons text-white text-xl'>close</span>
                    </button>
                </IconToolTip>
            </div>
        </div>
    );
};

export const GetComponentByType = ({ type, url, isSingle, isZoomable = false, className = 'h-full w-full object-contain max-h-full max-w-full', onImageClick = () => { }, onSave, is_duplicate }) => {
    const getImageComponent = (src) => <img onClick={onImageClick} src={process.env.PUBLIC_URL + src} className={`${className} rounded-lg`} alt='' />;
    const lookup = getLookupWitHDefaultValue({
        lookup: {
            image: isZoomable ? <ImageZoom showSaveButton onSave={onSave} imageUrl={url} /> : <img src={url} className={className} alt='' onClick={onImageClick} />,
            audio: (
                <div className='h-full w-full flex flex-col items-center justify-center'>
                    <img src={process.env.PUBLIC_URL + '/img/audio.png'} className={isSingle ? 'h-4/5 object-contain' : className} alt='' />
                    {isSingle && <audio className='w-full mt-4' src={url} controls />}
                </div>
            ),
            video: <video className={className} autoPlay loop src={url} muted controls />,
            pdf: <object data={url} aria-label='pdf' className={`${className} w-full`} />,
            text: getImageComponent('/img/txt-file.png'),
            xls: getImageComponent('/img/xls.png'),
        },
        defaultValue: <img src={process.env.PUBLIC_URL + '/img/unknownFileType.png'} className={`${className} rounded-lg`} alt='' onClick={onImageClick} />,
    });
    return lookup[type];
};

const FilesComponent = ({ attachments, viewMediaIndex, handleArrowClick, message_id }) => {
    let type = getFileType(attachments[viewMediaIndex]?.mime_type);
    let fileUrl = attachments[viewMediaIndex].url;
    let mimeType = attachments[viewMediaIndex]?.mime_type;
    let caption = attachments[viewMediaIndex].caption;
    let attachmentId = attachments[viewMediaIndex].id;
    const is_duplicate = attachments[viewMediaIndex].is_duplicate;
    let duplicate_references = attachments[viewMediaIndex].duplicate_references;
    if (is_duplicate && duplicate_references && duplicate_references.length > 0 && attachments?.[viewMediaIndex]?.chat_id) {
        duplicate_references = duplicate_references.filter((item) => item.ticket_id !== attachments[viewMediaIndex].chat_id);
    }
    const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
    const { src: renderSrc, isConverting: isConvertingHeic } = useHeicImage(fileUrl, mimeType);
    const dispatch = useDispatch();

    const handleDuplicateItemClick = (item) => {
        if (item.ticket_id && item.message_id) {
            const chatUrl = `/chats/${item.ticket_id}?messageId=${item.message_id}`;
            window.open(chatUrl, '_blank');
        }
        setShowDuplicatePopup(false);
    };

    const onSaveAttchment = (degree) => {
        if (degree === 0) return;
        let instruction = { operation: "rotate", operand: 90 * degree };
        dispatch(transformAttachmentAction({ attachmentId, conversationId: attachments[viewMediaIndex].chat_id, instructions: [instruction] }))
    };

    return (
        <div className='flex-1 flex flex-col relative'>
            <div className='flex-1 flex items-center justify-center relative'>
                <div className='absolute left-4 top-1/2 transform -translate-y-1/2 z-10'>
                    {attachments.length > 1 && <NavigationArrow handleArrowClick={handleArrowClick} direction='left' />}
                </div>
                <div className='w-full h-full flex items-center justify-center px-16 relative' style={{ maxHeight: '70vh' }}>
                    <GetComponentByType type={type} url={renderSrc} isSingle={true} isZoomable onSave={onSaveAttchment} />
                    {isConvertingHeic && (
                        <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm text-white text-sm font-medium z-10'>
                            Loading...
                        </div>
                    )}
                    {is_duplicate && duplicate_references && duplicate_references.length > 0 && (
                        <div
                            className='absolute top-[50px] left-5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg border border-red-400 backdrop-blur-sm bg-opacity-90 cursor-pointer hover:from-red-600 hover:to-red-700 transition-all duration-200'
                            onClick={() => setShowDuplicatePopup(true)}
                        >
                            <span className='flex items-center gap-1'>
                                {/* <span className='material-icons text-font14'>content_copy</span> */}
                                <span>Duplicate ({duplicate_references.length})</span>
                                <span className='material-icons text-font14'>expand_more</span>
                            </span>
                        </div>
                    )}
                    <RenderApproveReject image={attachments[viewMediaIndex]} message_id={message_id} />
                </div>
                <div className='absolute right-4 top-1/2 transform -translate-y-1/2 z-10'>
                    {attachments.length > 1 && <NavigationArrow handleArrowClick={handleArrowClick} direction='right' />}
                </div>
            </div>
            {caption && (
                <div className='py-3 px-6'>
                    <OverflowTip someLongText={caption} textClass='text-center text-white text-sm max-w-2xl mx-auto' />
                </div>
            )}

            {/* Duplicate References Popup */}
            {showDuplicatePopup && duplicate_references && duplicate_references.length > 0 && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm'>
                    <div className='bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 max-h-96 overflow-hidden'>
                        {/* Header */}
                        <div className='bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 flex items-center justify-between'>
                            <div className='flex items-center gap-2'>
                                {/* <span className='material-icons text-font14'>content_copy</span> */}
                                <h3 className='font-semibold text-lg'>Duplicate References ({duplicate_references.length})</h3>
                            </div>
                            <button
                                onClick={() => setShowDuplicatePopup(false)}
                                className='hover:bg-red-600 rounded-full p-1 transition-colors duration-200'
                            >
                                <span className='material-icons'>close</span>
                            </button>
                        </div>

                        {/* Content */}
                        <div className='max-h-80 overflow-y-auto'>
                            {duplicate_references.map((item, index) => (
                                <div
                                    key={item.id || index}
                                    className={`p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200 ${item.ticket_id && item.message_id ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                                    onClick={() => handleDuplicateItemClick(item)}
                                >
                                    <div className='flex items-start gap-3'>
                                        {/* Thumbnail */}
                                        <div className='flex-shrink-0'>
                                            <img
                                                src={item.url}
                                                alt='Duplicate'
                                                className='w-12 h-12 object-cover rounded-lg border border-gray-200'
                                            />
                                        </div>

                                        {/* Content */}
                                        <div className='flex-1 min-w-0'>
                                            <div className='flex items-center gap-2 mb-1'>
                                                <span className='material-icons text-sm text-gray-500'>image</span>
                                                <span className='text-sm font-medium text-gray-900 truncate'>
                                                    Reference #{index + 1}
                                                </span>
                                            </div>

                                            {(item.ticket_number || item.ticket_id) && (
                                                <div className='flex items-center gap-1 mb-1'>
                                                    <span className='material-icons text-xs text-blue-500'>confirmation_number</span>
                                                    <span className='text-xs text-gray-600 truncate'>
                                                        Ticket: {item.ticket_number || item.ticket_id}
                                                    </span>
                                                </div>
                                            )}

                                            {item.ticket_id && item.message_id ? (
                                                <div className='flex items-center gap-1 mt-2'>
                                                    <span className='material-icons text-xs text-blue-500'>open_in_new</span>
                                                    <span className='text-xs text-blue-500 font-medium'>Click to open chat</span>
                                                </div>
                                            ) : (
                                                <div className='flex items-center gap-1 mt-2'>
                                                    <span className='material-icons text-xs text-gray-400'>block</span>
                                                    <span className='text-xs text-gray-400'>No navigation available</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const NavigationArrow = ({ handleArrowClick, direction }) => {
    const angleLookup = { left: 'chevron_left', right: 'chevron_right' };

    return (
        <button
            className='h-12 w-12 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 backdrop-filter backdrop-blur-sm flex items-center justify-center transition-all duration-200 border border-gray-600 hover:border-white'
            onClick={() => handleArrowClick(direction)}
        >
            <span className='material-icons text-white text-3xl'>{angleLookup[direction]}</span>
        </button>
    );
};

const RenderApproveReject = ({ image, message_id }) => {
    const { is_approved, is_rejected, approver, rejector, chat_id, height, id } = image;
    const { chatId } = useParams();
    const serviceRequestData = useSelector((state) => state.serviceRequest.serviceRequestData);
    const supportTrackServiceRequest = useSelector((state) => state.serviceRequest.supportTrackServiceRequest);
    const serviceRequestList = useSelector((state) => state.serviceRequest.serviceRequestList);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);

    const showApprovalOptions = useMemo(() => {
        let id = chat_id;
        if (!chat_id) id = chatId;
        let serviceRequest = serviceRequestData[id];
        if (window.location.pathname.includes('support/track')) {
            serviceRequest = supportTrackServiceRequest;
        }
        if (!serviceRequest && serviceRequestList?.data?.length > 0) {
            serviceRequest = serviceRequestList.data.find((el) => el.id === id);
        }
        if (serviceRequest && serviceRequest.agents) {
            const isAdmin = serviceRequest.agents.find(({ role, agent }) => {
                return role === TicketAgentRole.SCM && agent.id === newLoggedUserData.user.id;
            });
            const isCustomer = serviceRequest.helpdesk_id === newLoggedUserData.org.id;
            return { showApprove: (isAdmin || isCustomer) && !is_approved && !is_rejected, isAdmin };
        }
        return { showApprove: false, isAdmin: false };
    }, [serviceRequestData, chat_id, chatId, newLoggedUserData, supportTrackServiceRequest, is_rejected, is_approved, serviceRequestList?.data]);

    return (
        <div className="absolute left-1/2 transform -translate-x-1/2 bottom-28 flex gap-3 z-10" style={{ maxHeight: getImageStyle(height) }}>
            <ApprovedRejectIcon
                showApprovalOptions={showApprovalOptions}
                is_approved={is_approved}
                is_rejected={is_rejected}
                approver={approver}
                rejector={rejector}
                conversationId={chat_id || chatId}
                messageId={message_id}
                attachmentId={id}
                isCardMessage={false}
                chat_id={chat_id || chatId}
            />
        </div>
    );
}
